import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, MapPin, AlertCircle, Wallet } from 'lucide-react';

interface Cart {
    subtotal: number;
    tax: number;
    total: number;
}

interface WalletBalance {
    balance: number;
    currency: string;
}

export default function CheckoutPage() {
    const [cart, setCart] = useState<Cart | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);
    const [vatRate, setVatRate] = useState('20'); // Default to 20%
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        address: '',
        city: '',
        postcode: '',
        paymentMethod: 'credit_card'
    });

    useEffect(() => {
        loadCart();
        loadWalletBalance();
        fetchVATRate();
    }, []);

    const fetchVATRate = async () => {
        try {
            const response = await fetch('/api/public/system-config/VAT_RATE');
            if (response.ok) {
                const data = await response.json();
                if (data?.value) {
                    // Convert to percentage (0.20 -> 20)
                    const rate = parseFloat(data.value);
                    setVatRate((rate * 100).toString());
                } else {
                    setVatRate('20'); // Fallback
                }
            }
        } catch (error) {
            console.error('Failed to fetch VAT rate:', error);
            setVatRate('20'); // Fallback to default
        }
    };

    const loadWalletBalance = async () => {
        try {
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/customer/wallet'), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok && data.wallet) {
                setWalletBalance({
                    balance: data.wallet.balance || 0,
                    currency: data.wallet.currency || 'GBP'
                });
            }
        } catch (error) {
            console.error('Load wallet balance error:', error);
        }
    };

    const loadCart = async () => {
        try {
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/cart'), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            setCart(data.cart);
        } catch (error) {
            console.error('Load cart error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const { getApiUrl } = await import('../config');
            const response = await fetch(getApiUrl('/checkout'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Checkout failed');
                return;
            }

            // Order created successfully
            navigate('/dashboard?order=' + (data.orderNumber || 'new'));
        } catch (error) {
            console.error('Checkout error:', error);
            setError('Failed to process checkout');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Checkout Form */}
                    <div className="md:col-span-2">
                        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded p-4 flex gap-3">
                                    <AlertCircle className="text-red-600 flex-shrink-0" />
                                    <p className="text-red-800">{error}</p>
                                </div>
                            )}

                            {/* Delivery Address */}
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <MapPin size={20} />
                                    Delivery Address
                                </h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="123 Main Street"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="London"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Postcode
                                            </label>
                                            <input
                                                type="text"
                                                name="postcode"
                                                value={formData.postcode}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="SW1A 1AA"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Payment Method */}
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <CreditCard size={20} />
                                    Payment Method
                                </h2>

                                {/* Wallet Balance Display */}
                                {walletBalance && walletBalance.balance > 0 && (
                                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm">
                                            <Wallet size={16} className="text-blue-600" />
                                            <span className="text-blue-900">
                                                Wallet Balance: <strong>£{(walletBalance.balance / 100).toFixed(2)}</strong>
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Payment Method Options */}
                                <div className="space-y-3">
                                    {/* Credit/Debit Card */}
                                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                           style={{borderColor: formData.paymentMethod === 'card' ? '#2563eb' : '#d1d5db', backgroundColor: formData.paymentMethod === 'card' ? '#eff6ff' : 'white'}}>
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value="card"
                                            checked={formData.paymentMethod === 'card'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 cursor-pointer"
                                        />
                                        <div className="ml-3">
                                            <p className="font-medium text-gray-900">Credit/Debit Card</p>
                                            <p className="text-sm text-gray-600">Pay with your card (Visa, Mastercard, etc.)</p>
                                        </div>
                                    </label>

                                    {/* Wallet Payment */}
                                    {walletBalance && walletBalance.balance > 0 && (
                                        <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                               style={{borderColor: formData.paymentMethod === 'wallet' ? '#2563eb' : ((walletBalance.balance / 100) >= (cart?.total || 0) ? '#d1d5db' : '#fecaca'), 
                                                       backgroundColor: formData.paymentMethod === 'wallet' ? '#eff6ff' : 'white',
                                                       opacity: (walletBalance.balance / 100) >= (cart?.total || 0) ? 1 : 0.6}}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="wallet"
                                                checked={formData.paymentMethod === 'wallet'}
                                                onChange={handleChange}
                                                disabled={(walletBalance.balance / 100) < (cart?.total || 0)}
                                                className="w-4 h-4 text-blue-600 cursor-pointer"
                                            />
                                            <div className="ml-3">
                                                <p className="font-medium text-gray-900">Wallet</p>
                                                <p className="text-sm text-gray-600">
                                                    Pay from your wallet balance (£{(walletBalance.balance / 100).toFixed(2)})
                                                    {(walletBalance.balance / 100) < (cart?.total || 0) && (
                                                        <span className="ml-2 text-red-600">Insufficient balance</span>
                                                    )}
                                                </p>
                                            </div>
                                        </label>
                                    )}

                                    {/* Wallet + Card Hybrid */}
                                    {walletBalance && walletBalance.balance > 0 && (walletBalance.balance / 100) < (cart?.total || 0) && (
                                        <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                               style={{borderColor: formData.paymentMethod === 'hybrid' ? '#2563eb' : '#d1d5db', backgroundColor: formData.paymentMethod === 'hybrid' ? '#eff6ff' : 'white'}}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="hybrid"
                                                checked={formData.paymentMethod === 'hybrid'}
                                                onChange={handleChange}
                                                className="w-4 h-4 text-blue-600 cursor-pointer"
                                            />
                                            <div className="ml-3">
                                                <p className="font-medium text-gray-900">Wallet + Card</p>
                                                <p className="text-sm text-gray-600">
                                                    Pay £{(walletBalance.balance / 100).toFixed(2)} from wallet, then £{(cart ? cart.total - (walletBalance.balance / 100) : 0).toFixed(2)} from card
                                                </p>
                                            </div>
                                        </label>
                                    )}

                                    {/* Bank Transfer */}
                                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                           style={{borderColor: formData.paymentMethod === 'bank_transfer' ? '#2563eb' : '#d1d5db', backgroundColor: formData.paymentMethod === 'bank_transfer' ? '#eff6ff' : 'white'}}>
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value="bank_transfer"
                                            checked={formData.paymentMethod === 'bank_transfer'}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-blue-600 cursor-pointer"
                                        />
                                        <div className="ml-3">
                                            <p className="font-medium text-gray-900">Bank Transfer</p>
                                            <p className="text-sm text-gray-600">Transfer funds directly to our account</p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {submitting ? 'Processing...' : 'Complete Order'}
                            </button>
                        </form>
                    </div>

                    {/* Order Summary */}
                    {cart && (
                        <div className="md:col-span-1">
                            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal</span>
                                        <span className="font-medium">£{cart.subtotal.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Tax ({vatRate}%)</span>
                                        <span className="font-medium">£{cart.tax.toFixed(2)}</span>
                                    </div>
                                    <div className="border-t pt-3 flex justify-between">
                                        <span className="font-semibold text-gray-900">Total</span>
                                        <span className="text-xl font-bold text-blue-600">£{cart.total.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
